/**
 * Arquivo: src/pages/Fiscal.tsx
 * Versão: 5.4.4
 * Data: 29/09/2026
 */
import { useEffect, useMemo, useState } from 'react'
import { Edit3, Save, X } from 'lucide-react'
import { listarTabelaGerencial, obterBaseAtiva, obterDadosAnaliticos, salvarProLabore } from '../services/financeiro'
import { usePeriod } from '../context/PeriodContext'
import { cashDate, movementValue } from '../utils/financialPresentation'
import MoneyInput from '../components/MoneyInput'
import type { BaseFinanceira, Lancamento } from '../types/database'

const moeda = (v:number) => v.toLocaleString('pt-BR',{style:'currency',currency:'BRL'})
const val = movementValue
const nomesMeses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']

function campo(row:Record<string,any>, ...nomes:string[]) {
  for (const nome of nomes) if (row[nome] !== undefined && row[nome] !== null) return row[nome]
  return undefined
}

export default function Fiscal(){
  const { periodo, label } = usePeriod()
  const [base,setBase]=useState<BaseFinanceira|null>(null)
  const [ls,setLs]=useState<Lancamento[]>([])
  const [pro,setPro]=useState<Record<string,any>[]>([])
  const [erro,setErro]=useState('')
  const [editando,setEditando]=useState<number|null>(null)
  const [valorPro,setValorPro]=useState(0)
  const [salvando,setSalvando]=useState(false)

  const carregar = async () => {
    try {
      const b=base || await obterBaseAtiva(); if(!b)return
      const d=await obterDadosAnaliticos(b.id)
      setBase(b); setLs(d.lancamentos)
      try { setPro(await listarTabelaGerencial('prolabore',b.id)) } catch { setPro([]) }
    } catch(e:any) { setErro(e.message||'Erro ao carregar apuração.') }
  }

  useEffect(()=>{ void carregar() },[])

  const anoSelecionado = periodo === 'ALL' ? new Date().getFullYear() : Number(periodo.slice(0,4))
  const mesSelecionado = periodo === 'ALL' ? null : Number(periodo.slice(5,7))

  const pjAno=useMemo(()=>ls.filter(l=>{
    if(!l.pj || !['PAGO','RECEBIDO'].includes(String(l.status).toUpperCase())) return false
    const chave=cashDate(l).slice(0,7)
    return Number(chave.slice(0,4))===anoSelecionado
  }),[ls,anoSelecionado])

  const dadosMeses = useMemo(()=>Array.from({length:12},(_,i)=>{
    const mes=i+1
    const chave=`${anoSelecionado}-${String(mes).padStart(2,'0')}`
    const movs=pjAno.filter(l=>cashDate(l).slice(0,7)===chave)
    const receita=movs.filter(l=>l.tipo==='RECEITA').reduce((a,l)=>a+val(l),0)
    const despesa=movs.filter(l=>l.tipo==='DESPESA').reduce((a,l)=>a+val(l),0)
    const reg=pro.find(p=>Number(campo(p,'ano','ANO'))===anoSelecionado && Number(campo(p,'mes','MES'))===mes)
    const proLabore=Number(campo(reg||{},'valor_liquido','VALOR_LIQUIDO','valor','VALOR')||0)
    return {mes,nome:nomesMeses[i],receita,despesa,proLabore,lucro:receita-despesa-proLabore}
  }),[pjAno,pro,anoSelecionado])

  const visiveis = mesSelecionado ? dadosMeses.filter(m=>m.mes===mesSelecionado) : dadosMeses
  const total = visiveis.reduce((acc,m)=>({
    receita:acc.receita+m.receita,
    despesa:acc.despesa+m.despesa,
    proLabore:acc.proLabore+m.proLabore,
    lucro:acc.lucro+m.lucro,
  }),{receita:0,despesa:0,proLabore:0,lucro:0})

  const abrirEdicao=(mes:number,valor:number)=>{setEditando(mes);setValorPro(Number(valor||0))}
  const gravar=async(mes:number)=>{
    if(!base)return
    setSalvando(true); setErro('')
    try{
      await salvarProLabore(base.id,{ano:anoSelecionado,mes,valor_liquido:Number(valorPro||0)})
      await carregar(); setEditando(null)
    }catch(e:any){setErro(e.message||'Erro ao salvar pró-labore.')}
    finally{setSalvando(false)}
  }

  return <div className="page">
    <div className="page-heading"><div><h1>Apuração Fiscal</h1><p>Regime de caixa para movimentações PJ • <strong>{label}</strong>.</p></div></div>
    {erro&&<div className="alert error">{erro}</div>}
    <div className="fiscal-note">Regime de caixa. São consideradas somente entradas e saídas realizadas marcadas como PJ. O pró-labore líquido é informado por competência.</div>

    <div className="cards-grid four-cards fiscal-summary">
      <div className="metric-card income-soft"><span>Entradas PJ</span><strong className="privacy-value">{moeda(total.receita)}</strong></div>
      <div className="metric-card expense-soft"><span>Saídas PJ</span><strong className="privacy-value">{moeda(total.despesa)}</strong></div>
      <div className="metric-card"><span>Pró-labore líquido</span><strong className="privacy-value">{moeda(total.proLabore)}</strong></div>
      <div className="metric-card dashboard-total"><span>Lucro apurado</span><strong className="privacy-value">{moeda(total.lucro)}</strong></div>
    </div>

    <div className="mobile-record-list mobile-only-list fiscal-mobile-list">
      {visiveis.map(m=><button key={m.mes} className="mobile-record-item fiscal-mobile-item" onClick={()=>abrirEdicao(m.mes,m.proLabore)}>
        <span className="mobile-record-main"><strong>{m.nome}</strong><small>Entrada {moeda(m.receita)} · Saída {moeda(m.despesa)}</small></span>
        <span className="mobile-record-side"><strong className={`privacy-value ${m.lucro<0?'expense':'income'}`}>{moeda(m.lucro)}</strong><small>Lucro</small></span>
      </button>)}
    </div>

    {editando!==null&&<section className="mobile-fiscal-editor mobile-only-list"><strong>Pró-labore líquido · {nomesMeses[editando-1]}</strong><div><MoneyInput value={valorPro} onChange={setValorPro}/><button className="button primary compact" disabled={salvando} onClick={()=>void gravar(editando)}>Salvar</button><button className="button ghost compact" onClick={()=>setEditando(null)}>Cancelar</button></div></section>}

    <section className="panel desktop-list-panel">
      <div className="panel-header"><h2>Apuração mensal</h2><span>{anoSelecionado}</span></div>
      <div className="table-wrap"><table className="fiscal-table"><thead><tr><th>Mês</th><th>Entradas PJ</th><th>Saídas PJ</th><th>Pró-labore líquido</th><th>Lucro</th></tr></thead>
      <tbody>{visiveis.map(m=><tr key={m.mes} className="desktop-clickable-row" tabIndex={0} role="button" aria-label={`Informar pró-labore líquido de ${m.nome}`} onClick={()=>{if(editando===null)abrirEdicao(m.mes,m.proLabore)}} onKeyDown={e=>{if((e.key==='Enter'||e.key===' ')&&editando===null){e.preventDefault();abrirEdicao(m.mes,m.proLabore)}}}><td>{m.nome}</td><td className="financial-value income privacy-value">{moeda(m.receita)}</td><td className="financial-value expense privacy-value">{moeda(m.despesa)}</td><td>{editando===m.mes?<div className="inline-prolabore"><MoneyInput value={valorPro} onChange={setValorPro}/><button className="table-action" disabled={salvando} onClick={()=>void gravar(m.mes)} title="Salvar"><Save size={14}/></button><button className="table-action" onClick={()=>setEditando(null)} title="Cancelar"><X size={14}/></button></div>:<div className="prolabore-cell"><span className="privacy-value">{moeda(m.proLabore)}</span></div>}</td><td className={`financial-value privacy-value ${m.lucro<0?'expense':'income'}`}>{moeda(m.lucro)}</td></tr>)}</tbody></table></div>
    </section>
  </div>
}

